import { List, useTable, DateField } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import {
  Table,
  Row,
  Col,
  Tag,
  Card,
  Space,
  Typography,
  Segmented,
  Select,
} from "antd";
import {
  DollarOutlined,
  ThunderboltOutlined,
  AudioOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useMemo, useState } from "react";
import type { AiMetric } from "@/lib/types";
import { StatCard } from "@/components/StatCard";
import {
  MelAreaChart,
  MelDonutChart,
  MelBarChart,
} from "@/components/charts/MelCharts";
import { usd, intFmt } from "@/lib/format";

const { Text } = Typography;

const KIND_COLOR: Record<AiMetric["kind"], string> = {
  stt: "blue",
  llm: "geekblue",
  embedding: "purple",
};

const KIND_LABEL: Record<AiMetric["kind"], string> = {
  stt: "STT (transcrição)",
  llm: "LLM (classificação)",
  embedding: "Embedding",
};

const ALL = "__all__";

/**
 * /admin/ai-metrics — DASHBOARD de uso e CUSTO de IA.
 *
 * Como obtém os dados (MOCK MODE): `useList` com `pagination: { mode: "off" }`
 * puxa TODAS as linhas de ai-metrics dos fixtures (1 linha por dia × provider ×
 * kind, 30 dias) e agregamos no cliente para os cards e gráficos. A tabela bruta
 * usa `useTable` paginado normalmente. O filtro de provider (client-side) recorta
 * todas as agregações sem ir à rede. Quando a API real estiver pronta basta
 * desligar o VITE_MOCK — o mesmo resource "ai-metrics" passa a ler de
 * GET /admin/ai-metrics (simple-rest).
 */
export const AiMetricsList: React.FC = () => {
  const [view, setView] = useState<"cost" | "requests">("cost");
  const [provider, setProvider] = useState<string>(ALL);

  const { tableProps } = useTable<AiMetric>({
    syncWithLocation: true,
    sorters: { initial: [{ field: "date", order: "desc" }] },
  });

  // Todas as linhas para agregação (sem paginar).
  const { data: allData, isLoading } = useList<AiMetric>({
    resource: "ai-metrics",
    pagination: { mode: "off" },
  });
  const allRows = useMemo(() => allData?.data ?? [], [allData]);

  // Lista de providers para o filtro.
  const providers = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.provider))).sort(),
    [allRows],
  );

  // Linhas após o filtro de provider (recorta cards + gráficos).
  const rows = useMemo(
    () => (provider === ALL ? allRows : allRows.filter((r) => r.provider === provider)),
    [allRows, provider],
  );

  // ── KPIs do período ──
  const kpis = useMemo(() => {
    let cost = 0;
    let requests = 0;
    let audioSec = 0;
    let fallback = 0;
    for (const r of rows) {
      cost += r.cost_usd ?? 0;
      requests += r.requests ?? 0;
      audioSec += r.audio_seconds ?? 0;
      fallback += r.fallback_count ?? 0;
    }
    const fallbackRate = requests ? (fallback / requests) * 100 : 0;
    return {
      cost,
      requests,
      audioMin: Math.round(audioSec / 60),
      fallbackRate,
    };
  }, [rows]);

  // ── Série por dia (área empilhada por provider) — custo OU requests ──
  const byDay = useMemo(() => {
    const provs = Array.from(new Set(rows.map((r) => r.provider))).sort();
    const map = new Map<string, Record<string, number | string>>();
    for (const r of rows) {
      const cur = map.get(r.date) ?? { date: r.date.slice(5) };
      const add = view === "cost" ? r.cost_usd : r.requests;
      cur[r.provider] = Number(((cur[r.provider] as number) ?? 0) + add);
      map.set(r.date, cur);
    }
    const data = Array.from(map.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
    return {
      data,
      series: provs.map((p) => ({ key: p, name: p })),
    };
  }, [rows, view]);

  // ── Custo por provider (rosca) ──
  const byProvider = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) map.set(r.provider, (map.get(r.provider) ?? 0) + r.cost_usd);
    return Array.from(map.entries()).map(([name, value]) => ({
      name,
      value: Math.round(value * 100) / 100,
    }));
  }, [rows]);

  // ── Custo por tipo (kind) — barras horizontais ──
  const byKind = useMemo(() => {
    const map = new Map<AiMetric["kind"], number>();
    for (const r of rows) map.set(r.kind, (map.get(r.kind) ?? 0) + r.cost_usd);
    return Array.from(map.entries())
      .map(([k, value]) => ({
        kind: KIND_LABEL[k] ?? k,
        cost: Math.round(value * 100) / 100,
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [rows]);

  const valueFmt = view === "cost" ? (v: number) => usd.format(v) : (v: number) => intFmt.format(v);

  return (
    <List
      title="IA · Uso & Custo"
      headerButtons={
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Provider
          </Text>
          <Select
            size="small"
            value={provider}
            style={{ minWidth: 140 }}
            onChange={setProvider}
            options={[
              { label: "Todos", value: ALL },
              ...providers.map((p) => ({ label: p, value: p })),
            ]}
          />
        </Space>
      }
    >
      {/* Cards de CUSTO — agregados dos fixtures (recortados pelo provider) */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Custo de IA (30d)"
            value={kpis.cost}
            precision={2}
            prefix={<DollarOutlined />}
            hint="Σ cost_usd no período"
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Requests LLM/STT"
            value={kpis.requests}
            prefix={<ThunderboltOutlined />}
            hint="Σ requests"
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Áudio transcrito"
            value={kpis.audioMin}
            prefix={<AudioOutlined />}
            suffix="min"
            hint="Σ audio_seconds / 60"
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            title="Taxa de fallback"
            value={kpis.fallbackRate}
            precision={1}
            prefix={<WarningOutlined />}
            suffix="%"
            hint="fallback / requests"
            loading={isLoading}
          />
        </Col>
      </Row>

      {/* Gráficos */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={16}>
          <Card
            className="mel-glass"
            bordered={false}
            title={view === "cost" ? "Custo de IA por dia (USD)" : "Requests por dia"}
            loading={isLoading}
            extra={
              <Segmented
                size="small"
                value={view}
                onChange={(v) => setView(v as "cost" | "requests")}
                options={[
                  { label: "Custo", value: "cost" },
                  { label: "Requests", value: "requests" },
                ]}
              />
            }
          >
            <MelAreaChart
              data={byDay.data}
              xKey="date"
              series={byDay.series}
              valueFormatter={valueFmt}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            className="mel-glass"
            bordered={false}
            title="Custo por provider"
            loading={isLoading}
          >
            <MelDonutChart data={byProvider} valueFormatter={(v) => usd.format(v)} />
          </Card>
        </Col>
      </Row>

      {/* Custo por tipo (kind) */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <Card
            className="mel-glass"
            bordered={false}
            title="Custo por tipo de operação"
            loading={isLoading}
          >
            <MelBarChart
              data={byKind}
              xKey="kind"
              series={[{ key: "cost", name: "Custo (USD)" }]}
              valueFormatter={(v) => usd.format(v)}
              horizontal
              height={220}
            />
          </Card>
        </Col>
      </Row>

      {/* Tabela bruta — ligada ao dataProvider (paginada) */}
      <Card className="mel-glass" bordered={false} title="Linhas de uso">
        <Table {...tableProps} rowKey="id" scroll={{ x: 1100 }}>
          <Table.Column
            dataIndex="date"
            title="Data"
            sorter
            render={(v: string) => <DateField value={v} format="DD/MM/YYYY" />}
          />
          <Table.Column
            dataIndex="kind"
            title="Tipo"
            render={(k: AiMetric["kind"]) => (
              <Tag color={KIND_COLOR[k] ?? "default"}>{k}</Tag>
            )}
          />
          <Table.Column dataIndex="provider" title="Provider" sorter />
          <Table.Column
            dataIndex="user_id"
            title="Usuário"
            render={(v: string | null) => v ?? <Tag>agregado</Tag>}
          />
          <Table.Column
            dataIndex="requests"
            title="Reqs"
            sorter
            render={(v: number) => intFmt.format(v ?? 0)}
          />
          <Table.Column<AiMetric>
            title="Tokens (in/out)"
            render={(_, row) => (
              <Space size={4}>
                <Text>{intFmt.format(row.tokens_in ?? 0)}</Text>
                <Text type="secondary">/</Text>
                <Text>{intFmt.format(row.tokens_out ?? 0)}</Text>
              </Space>
            )}
          />
          <Table.Column
            dataIndex="cost_usd"
            title="Custo"
            sorter
            render={(v: number) => usd.format(v ?? 0)}
          />
          <Table.Column
            dataIndex="fallback_count"
            title="Fallbacks"
            render={(v: number) =>
              v > 0 ? <Tag color="orange">{v}</Tag> : <Tag>0</Tag>
            }
          />
          <Table.Column
            dataIndex="avg_latency_ms"
            title="Latência"
            sorter
            render={(v: number) => `${intFmt.format(v ?? 0)} ms`}
          />
        </Table>
      </Card>
    </List>
  );
};
